import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  FormControl,
  FormLabel,
  Stack,
} from '@mui/joy';
import {
  DownloadSimple as Download,
  Printer as Print,
  CaretDown as ExpandMore,
  CaretUp as ExpandLess,
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
  Calendar as CalendarToday,
  Buildings as Business,
  CreditCard,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type { CashFlowStatement, CashFlowSection, CashFlowLine } from '../../../types/accounting';

export const CashFlow: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlowStatement | null>(null);
  const [comparativeCashFlow, setComparativeCashFlow] = useState<CashFlowStatement | null>(null);
  
  // Report parameters
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [compareStartDate, setCompareStartDate] = useState<string | null>(null);
  const [compareEndDate, setCompareEndDate] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['operating', 'investing', 'financing'])
  );
  
  useEffect(() => {
    fetchCashFlow();
  }, [startDate, endDate]);

  useEffect(() => {
    if (showComparison && compareStartDate && compareEndDate) {
      fetchComparativeCashFlow();
    }
  }, [compareStartDate, compareEndDate, showComparison]);

  const fetchCashFlow = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiAccountingClient.getCashFlow({
        start_date: startDate,
        end_date: endDate,
      });
      setCashFlow(response);
    } catch (err) {
      console.error('Failed to fetch cash flow:', err);
      setError('Failed to load cash flow statement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparativeCashFlow = async () => {
    if (!compareStartDate || !compareEndDate) return;
    
    try {
      const response = await apiAccountingClient.getCashFlow({
        start_date: compareStartDate,
        end_date: compareEndDate,
      });
      setComparativeCashFlow(response);
    } catch (err) {
      console.error('Failed to fetch comparative cash flow:', err);
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await apiAccountingClient.exportCashFlow({
        start_date: startDate,
        end_date: endDate,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cash_flow_${startDate}_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export:', err);
      alert('Failed to export cash flow statement');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return '-';
    const isNegative = amount < 0;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(Math.abs(amount));
    return isNegative ? `(${formatted})` : formatted;
  };

  const calculateChange = (current: number, previous: number | undefined) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / Math.abs(previous)) * 100;
    return change;
  };

  const renderCashFlowLine = (
    line: CashFlowLine,
    compareLine?: CashFlowLine,
    indent: number = 0
  ) => {
    const change = compareLine ? calculateChange(line.amount, compareLine.amount) : null;
    
    return (
      <tr key={line.id}>
        <td style={{ paddingLeft: `${indent * 20 + 8}px` }}>
          <Typography 
            level="body-sm" 
            sx={{ 
              fontWeight: line.is_total ? 'bold' : 'normal',
              fontStyle: line.is_header ? 'italic' : 'normal',
            }}
          >
            {line.description}
          </Typography>
        </td>
        <td align="right">
          <Typography 
            level="body-sm" 
            sx={{ 
              fontWeight: line.is_total ? 'bold' : 'normal',
              color: line.amount < 0 ? 'danger.500' : 'text.primary',
            }}
          >
            {formatCurrency(line.amount)}
          </Typography>
        </td>
        {showComparison && (
          <>
            <td align="right">
              <Typography 
                level="body-sm" 
                sx={{ 
                  color: 'text.secondary',
                  fontStyle: compareLine?.amount && compareLine.amount < 0 ? 'normal' : 'normal',
                }}
              >
                {formatCurrency(compareLine?.amount)}
              </Typography>
            </td>
            <td align="right">
              {change !== null && Math.abs(change) > 0.01 && (
                <Chip
                  size="sm"
                  color={change > 0 ? 'success' : 'danger'}
                  startDecorator={change > 0 ? <TrendingUp /> : <TrendingDown />}
                >
                  {change.toFixed(1)}%
                </Chip>
              )}
            </td>
          </>
        )}
      </tr>
    );
  };

  const renderSection = (
    title: string,
    section: CashFlowSection | undefined,
    compareSection: CashFlowSection | undefined,
    sectionKey: string,
    icon: React.ReactNode
  ) => {
    if (!section) return null;
    
    const isExpanded = expandedSections.has(sectionKey);
    const change = compareSection ? calculateChange(section.total, compareSection.total) : null;

    return (
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1,
            bgcolor: 'background.level1',
            borderRadius: 'sm',
            cursor: 'pointer',
          }}
          onClick={() => toggleSection(sectionKey)}
        >
          <Typography level="title-md" sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="sm" sx={{ mr: 1 }}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            {icon}
            <Box component="span" sx={{ ml: 1 }}>{title}</Box>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              level="title-md"
              sx={{ color: section.total < 0 ? 'danger.500' : 'text.primary' }}
            >
              {formatCurrency(section.total)}
            </Typography>
            {showComparison && change !== null && Math.abs(change) > 0.01 && (
              <Chip
                size="sm"
                color={change > 0 ? 'success' : 'danger'}
                startDecorator={change > 0 ? <TrendingUp /> : <TrendingDown />}
              >
                {change.toFixed(1)}%
              </Chip>
            )}
          </Box>
        </Box>

        {isExpanded && (
          <Box sx={{ overflowX: 'auto', mt: 2 }}>
            <Table size="sm" sx={{ minWidth: { xs: 480, md: 0 } }}>
              <tbody>
                {section.lines.map((line) => {
                  const compareLine = compareSection?.lines.find(
                    (cl) => cl.description === line.description
                  );
                  return renderCashFlowLine(line, compareLine, line.level || 0);
                })}
              </tbody>
            </Table>
          </Box>
        )}
      </Box>
    );
  };

  const setPreviousPeriod = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    
    const newEnd = new Date(start);
    newEnd.setDate(newEnd.getDate() - 1);
    const newStart = new Date(newEnd);
    newStart.setDate(newStart.getDate() - daysDiff);
    
    setCompareStartDate(newStart.toISOString().split('T')[0]);
    setCompareEndDate(newEnd.toISOString().split('T')[0]);
    setShowComparison(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !cashFlow) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        {error || 'Failed to load cash flow statement'}
      </Alert>
    );
  }

  return (
    <Box className="cash-flow-report">
      {/* Header Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 1.5, mb: 3 }}>
            <Box>
              <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined } }}>
                Cash Flow Statement
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                For the period {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ '& > button': { flex: { xs: 1, md: '0 0 auto' } } }}>
              <Button
                startDecorator={<Download />}
                variant="outlined"
                color="primary"
                onClick={handleExportExcel}
              >
                Export Excel
              </Button>
              <Button
                startDecorator={<Print />}
                variant="outlined"
                color="primary"
                onClick={handlePrint}
              >
                Print
              </Button>
            </Stack>
          </Box>

          {/* Report Parameters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
            <FormControl>
              <FormLabel>Start Date</FormLabel>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel>End Date</FormLabel>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              />
            </FormControl>

            <Button
              variant="soft"
              onClick={setPreviousPeriod}
              startDecorator={<CalendarToday />}
            >
              Compare Previous Period
            </Button>

            {showComparison && (
              <Button
                variant="plain"
                color="neutral"
                onClick={() => {
                  setShowComparison(false);
                  setCompareStartDate(null);
                  setCompareEndDate(null);
                  setComparativeCashFlow(null);
                }}
              >
                Clear Comparison
              </Button>
            )}
          </Stack>

          {showComparison && compareStartDate && compareEndDate && (
            <Typography level="body-sm" sx={{ mt: 2, color: 'text.secondary' }}>
              Comparing with: {new Date(compareStartDate).toLocaleDateString()} to {new Date(compareEndDate).toLocaleDateString()}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Cash Flow Content */}
      <Card>
        <CardContent>
          {/* Operating Activities */}
          {renderSection(
            'Cash Flows from Operating Activities',
            cashFlow.operating_activities,
            comparativeCashFlow?.operating_activities,
            'operating',
            <Business size={20} />
          )}

          <Divider sx={{ my: 2 }} />

          {/* Investing Activities */}
          {renderSection(
            'Cash Flows from Investing Activities',
            cashFlow.investing_activities,
            comparativeCashFlow?.investing_activities,
            'investing',
            <TrendingUp size={20} />
          )}

          <Divider sx={{ my: 2 }} />

          {/* Financing Activities */}
          {renderSection(
            'Cash Flows from Financing Activities',
            cashFlow.financing_activities,
            comparativeCashFlow?.financing_activities,
            'financing',
            <CreditCard size={20} />
          )}

          <Divider sx={{ my: 3 }} />

          {/* Summary */}
          <Box sx={{ bgcolor: 'background.level1', p: 2, borderRadius: 'sm' }}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="title-lg">Net Change in Cash</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography 
                    level="title-lg"
                    sx={{ color: cashFlow.net_change < 0 ? 'danger.500' : 'text.primary' }}
                  >
                    {formatCurrency(cashFlow.net_change)}
                  </Typography>
                  {showComparison && comparativeCashFlow && (
                    <Chip
                      color={
                        (cashFlow.net_change - comparativeCashFlow.net_change) > 0 
                          ? 'success' 
                          : 'danger'
                      }
                      startDecorator={
                        (cashFlow.net_change - comparativeCashFlow.net_change) > 0 
                          ? <TrendingUp /> 
                          : <TrendingDown />
                      }
                    >
                      {formatCurrency(Math.abs(cashFlow.net_change - comparativeCashFlow.net_change))}
                    </Chip>
                  )}
                </Box>
              </Box>
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Beginning Cash</Typography>
                <Typography>{formatCurrency(cashFlow.beginning_cash)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography level="title-md">Ending Cash</Typography>
                <Typography 
                  level="title-md"
                  sx={{ color: cashFlow.ending_cash < 0 ? 'danger.500' : 'text.primary' }}
                >
                  {formatCurrency(cashFlow.ending_cash)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Report Metadata */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              Generated on {new Date().toLocaleString()} • 
              Report Period: {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
              {showComparison && compareStartDate && compareEndDate && 
                ` • Comparing with ${new Date(compareStartDate).toLocaleDateString()} to ${new Date(compareEndDate).toLocaleDateString()}`
              }
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Print Styles */}
      <style>{`
        @media print {
          .cash-flow-report {
            padding: 20px;
          }
          button, .MuiButton-root {
            display: none !important;
          }
          input[type="date"] {
            display: none !important;
          }
        }
      `}</style>
    </Box>
  );
};